
// Sidebar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import sidebar from "../data/sidebar";
import { useTranslation } from 'react-i18next';
import { getJson } from '../services/http';
import { getSessionLocal } from '../services/sessionLocal';
import { can } from '../services/rolePermissions';
import TenantSelectionModal from './shared/TenantSelectionModal';

// Map sidebar child titles to { resource, action } for permission gating.
// Only entries that need gating are listed — everything else is shown freely.
const SIDEBAR_PERMISSION_MAP = {
  'Add User':           { resource: 'tenantUsers', action: 'add' },
  'Tenant Users':       { resource: 'tenantUsers', action: 'view' },
  // Transaction Report is the tenant audit log — owner/admin only, matching
  // the server gate on tenantTransactionRoutes.js. 'tenantUsers/delete' is
  // owner+admin only under the default permission matrix.
  'Transaction Report': { resource: 'tenantUsers', action: 'delete' },
};

const Sidebar = ({ tenantId }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false); // ← collapsed by default
  const [expandedMenu, setExpandedMenu] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const navigate = useNavigate();

  // Check super-admin access once on mount
  useEffect(() => {
    getJson('/api/super-admin/check-access')
      .then(() => setIsSuperAdmin(true))
      .catch(() => {}); // silently fail for non-admin tenants
  }, []);

  // Fetch tenants this user can access (cache in localStorage for reload resilience)
  useEffect(() => {
    // Load cached tenants first so the menu renders immediately after reload
    try {
      const cached = JSON.parse(localStorage.getItem('myTenants') || '[]');
      if (cached.length > 0) setTenants(cached);
    } catch {}

    getJson('/api/users/my-tenants')
      .then((data) => {
        if (data.success && data.tenants) {
          setTenants(data.tenants);
          localStorage.setItem('myTenants', JSON.stringify(data.tenants));
        }
      })
      .catch(() => {});
  }, []);

  const toggleSidebar = () => setIsOpen((v) => !v);
  const toggleSubmenu = (index) =>
    setExpandedMenu((prev) => ({ ...prev, [index]: !prev[index] }));

  // Keep existing logout behavior by navigating to /logout
  const handleLogout = () => {
    navigate("/logout");
  };

  const handleSubmenuClick = (child) => {
    if (child.action === 'switchTenant') {
      setShowTenantModal(true);
      return;
    }
    navigate(child.path);
  };

  const handleTenantSwitch = async (selectedTenantId) => {
    setShowTenantModal(false);

    // Re-issue the JWT bound to the target tenant. Without this, every
    // tenant-scoped route still sees the old tenant id from the bearer
    // token and rejects with 403 (e.g. external-upload, members).
    try {
      const session = getSessionLocal();
      // Auth rides on the httpOnly authToken cookie (sent automatically
      // because credentials: 'include' below). The previous version of
      // this call also injected `Authorization: Bearer <localStorage
      // .authToken>` — Phase 2b removed that path; the cookie is the
      // sole auth transport now. The X-* headers are informational only
      // and the server doesn't trust them.
      const res = await fetch('/api/users/switch-tenant', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': session.userId ?? '',
          'X-Username': session.username ?? '',
        },
        body: JSON.stringify({ tenantId: selectedTenantId, userId: session.userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        // PHASE 2 — DO NOT WRITE data.token TO LOCALSTORAGE. The
        // /switch-tenant endpoint refreshes the httpOnly authToken cookie
        // (see server/routes/userRoutes.js setAuthCookie call), so the
        // new tenant's JWT rides on the browser's cookie store starting
        // with the very next request. Role / userId stay in localStorage
        // because they drive UI state, not authentication.
        if (data.user?.role) localStorage.setItem('role', data.user.role);
        if (data.user?.id) localStorage.setItem('userId', String(data.user.id));
      } else {
        console.warn('[Sidebar] switch-tenant failed:', data?.message || res.status);
      }
    } catch (err) {
      console.warn('[Sidebar] switch-tenant request errored:', err);
    }

    // Update localStorage with the new tenant context
    localStorage.setItem('tenantid', selectedTenantId);
    localStorage.setItem('tenantId', String(selectedTenantId));
    try {
      const me = JSON.parse(localStorage.getItem('me') || '{}');
      me.tenantId = selectedTenantId;
      localStorage.setItem('me', JSON.stringify(me));
      window.__ME__ = me;
    } catch {}
    // Navigate to the new tenant's dashboard and reload to re-initialize
    window.location.href = `/${selectedTenantId}/dashboard`;
  };

  const hasMultipleTenants = tenants.length > 1;

  // Filter sidebar children based on role permissions
  const session = getSessionLocal();
  const filterByPermission = (items) =>
    items.map(group => {
      if (!group.childrens) return group;
      const filtered = group.childrens.filter(child => {
        const gate = SIDEBAR_PERMISSION_MAP[child.title];
        if (!gate) return true; // no gate → always show
        return can(session.role, gate.resource, gate.action);
      });
      return { ...group, childrens: filtered };
    }).filter(group => !group.childrens || group.childrens.length > 0);

  return (
    <>
      <div className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
        {/* Inner flex container so we can pin Logout to the bottom */}
        <div
          className="sidebar-inner"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Top section: toggle + regular menu groups */}
          <div>
            <div className="icon-div">
              {isOpen ? (
                <i
                  className="bi bi-caret-left-fill"
                  onClick={toggleSidebar}
                ></i>
              ) : (
                <i
                  className="bi bi-caret-right-fill"
                  onClick={toggleSidebar}
                ></i>
              )}
            </div>

            <ul className="menu-list">
              {filterByPermission(sidebar(tenantId, { isSuperAdmin, hasMultipleTenants })).map((menuItem, index) => (
                <li key={index} className="menu-item">
                  <div
                    className="menu-title"
                    onClick={() =>
                      menuItem.childrens && toggleSubmenu(index)
                    }
                    style={{
                      cursor: menuItem.childrens ? "pointer" : "default",
                    }}
                  >
                    <i className={`bi ${menuItem.icon}`}></i>
                    <span className="menu-label">{menuItem.title}</span>
                    {menuItem.childrens && (
                      <i
                        className={`bi ${
                          expandedMenu[index]
                            ? "bi-chevron-down"
                            : "bi-chevron-right"
                        } submenu-toggle-icon`}
                        style={{ marginLeft: "auto" }}
                      />
                    )}
                  </div>

                  {menuItem.childrens && expandedMenu[index] && (
                    <ul className="submenu-list">
                      {menuItem.childrens.map((child, idx) => (
                        <li key={idx} className="submenu-item">
                          {child.action ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSubmenuClick(child);
                              }}
                            >
                              <i className={`bi ${child.icon}`} />
                              <span className="menu-label">{child.title}</span>
                            </a>
                          ) : (
                            <Link to={child.path}>
                              <i className={`bi ${child.icon}`} />
                              <span className="menu-label">{child.title}</span>
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Bottom Logout icon as a top-level menu item */}
          <div
            className="logout-container"
            style={{
              marginTop: "auto",
              padding: "0.5rem 0",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div className="menu-item">
              <div
                className="menu-title"
                onClick={handleLogout}
                style={{ cursor: "pointer" }}
              >
                <i className="bi bi-door-closed-fill"></i>
                <span className="menu-label">{t('auth.logoutText')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTenantModal && (
        <TenantSelectionModal
          tenants={tenants}
          onSelect={handleTenantSwitch}
          onCancel={() => setShowTenantModal(false)}
          isLoading={false}
        />
      )}
    </>
  );
};

export default Sidebar;
